import React, { useRef } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import {
  useGetBankTransactionsByAccountQuery,
  useGetBankTransactionsOverviewByAccountQuery,
} from '@/store/transactionsApi';
import CashFlowTable from './CashFlowTable';
import { Box, CircularProgress, Alert } from '@mui/material';
import type { GridContainerSize } from '@/components/grids/GridContainer';

interface CombinedCashFlowViewProps {
  accountId: number;
  startDate: string;
  endDate: string;
  investorId?: number;
  size?: GridContainerSize;
}

const CombinedCashFlowView: React.FC<CombinedCashFlowViewProps> = ({
  accountId,
  startDate,
  endDate,
  investorId,
  size,
}) => {
  const { t } = useLocalization();
  const bulkEditButtonContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    data: transactionsData,
    error: transactionsError,
    isLoading: isLoadingTransactions,
  } = useGetBankTransactionsByAccountQuery({
    accountId,
    startDate,
    endDate,
    InvestorId: investorId,
  });

  const {
    data: overviewData,
    error: overviewError,
    isLoading: isLoadingOverview,
  } = useGetBankTransactionsOverviewByAccountQuery({
    accountId,
    startDate,
    endDate,
    InvestorId: investorId,
  });

  // Handle loading state
  if (isLoadingTransactions || isLoadingOverview) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 3,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Handle error state
  if (transactionsError || overviewError) {
    return (
      <Alert
        severity='error'
        sx={{ m: 2 }}
      >
        {t(
          'cashFlow.errorLoadingTransactions',
          'There was an error loading transaction data. Please try again later.',
        )}
      </Alert>
    );
  }

  // Handle case where data might not be available
  if (!transactionsData || !overviewData) {
    return (
      <Alert
        severity='info'
        sx={{ m: 2 }}
      >
        {t(
          'cashFlow.noTransactionData',
          'No transaction data available for the selected period.',
        )}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Container for bulk edit button portal */}
      <div ref={bulkEditButtonContainerRef} />
      {/* Conditionally render summary information */}
      {overviewData.NumberUncatorizedInDateRange > 0 && (
        <Alert
          severity='info'
          sx={{ mb: 2 }}
        >
          {t('cashFlow.uncategorizedTransactionsInfo', {
            count: overviewData.NumberUncatorizedInDateRange,
            defaultValue:
              'You have {{count}} uncategorized transaction(s). You can categorize them below.',
          })}
        </Alert>
      )}

      {/* Render the transaction list */}
      <CashFlowTable
        transactions={transactionsData.transactions}
        bulkEditButtonContainer={bulkEditButtonContainerRef.current}
        investorId={investorId}
        size={size}
      />
    </Box>
  );
};

export default CombinedCashFlowView;
